import React, { useEffect, useRef, useState } from "react"
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  AccordionBody,
  AccordionItem,
  UncontrolledTooltip,
  Button,
  Accordion,
  FormGroup,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import dayjs from "dayjs"
import {
  dateSorter,
  defaultPageSizeOptions,
  showToast,
  stringSorter,
  downloadExcel,
  formatDateSimple,
  booleanSorter,
  numberSorter,
  getTrackingLink,
} from "utils/general"
import CustomRangePicker from "components/DatePickers/RangePicker"
import { Table as AntdTable, Input as AntdInput, Switch, Select, Popconfirm, Tooltip, } from "antd"
import { tableDefaultHeight } from "constants/layout"
import { addReturnsEntry, getReturnsAndDeliveries } from "helpers/backend_helper"
import ReturnsForm from "./ReturnsForm"
const { Search } = AntdInput


const ReturnsManagement = props => {
  const [data, setData] = useState([])
  const [ongoingSearch, setOngoingSearch] = useState(false)
  const [ongoingAction, setOngoingAction] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [filtersOpen, setFiltersOpen] = useState("")
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("day").subtract(2, "months"),
    dayjs(),
  ])
  const [title] = useState(props.t("Returns and deliveries"))
  const [addConfirm, setAddConfirm] = useState(false)
  const [searchTriggers, setSearchTriggers] = useState(0) // Delayed search
  const [showDrawer, setShowDrawer] = useState(false)
  const [selectedRowKey, setSelectedRowKey] = useState()
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [fixedColumns, setFixedColumns] = useState(false)
  const [itemOptions, setItemOptions] = useState([])
  const [reasonOptions, setReasonOptions] = useState([])

  const [returnsStatusFilter, setReturnsStatusFilter] = useState()
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState()
  const [reasonFilter, setReasonFilter] = useState([])
  const [missingFilter, setMissingFilter] = useState()
  const [invoicingFilter, setInvoicingFilter] = useState()
  const [elisaFilter, setElisaFilter] = useState()
  const [highlightFilter, setHighlightFilter] = useState()
  const [returnsItemsFilter, setReturnsItemsFilter] = useState([])
  const [deliveryItemsFilter, setDeliveryItemsFilter] = useState([])
  const [idFilter, setIdFilter] = useState()

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"

    if (localStorage.getItem("fixed_columns") == "true") {
      setFixedColumns(true)
    }

    let opts = [
      { label: props.t("Home gateway"), value: "home_gw" },
      { label: props.t("At home set"), value: "home_set" },
      { label: props.t("Home sensor"), value: "home_node" },
      { label: props.t("Home accessory"), value: "home_accessory" },
      { label: props.t("Tracker"), value: "tracker_device" },
      { label: props.t("Tracker accessory"), value: "tracker_accessory" },
      { label: props.t("Safety wristwatch"), value: "wristwatch_device" },
      { label: props.t("Safety wristwatch accessory"), value: "wristwatch_accessory" },
      { label: props.t("Carephone"), value: "carephone_main" },
      { label: props.t("Carephone accessory"), value: "carephone_accessory" },
      { label: props.t("Video device"), value: "video_device" },
      { label: props.t("Video accessory"), value: "video_accessory" },
      { label: props.t("Smartlock"), value: "smartlock_device" },
      { label: props.t("Smartlock accessory"), value: "smartlock_accessory" },
      { label: props.t("BatteryAlt"), value: "batteries" },
      { label: props.t("Other"), value: "other" },
    ]
    opts.sort((a, b) => a.label.localeCompare(b.label))
    setItemOptions(opts)

    let opts2 = [
      { label: props.t(getReturnsReasonText(1)), value: 1 },
      { label: props.t(getReturnsReasonText(2)), value: 2 },
      { label: props.t(getReturnsReasonText(3)), value: 3 },
      { label: props.t(getReturnsReasonText(4)), value: 4 },
      { label: props.t(getReturnsReasonText(5)), value: 5 },
      { label: props.t(getReturnsReasonText(6)), value: 6 },
      { label: props.t(getReturnsReasonText(7)), value: 7 },
      { label: props.t(getReturnsReasonText(8)), value: 8 },
      { label: props.t(getReturnsReasonText(9)), value: 9 },
    ]
    opts2.sort((a, b) => a.label.localeCompare(b.label))
    setReasonOptions(opts2)

    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    getData()
  }, [dateRange, searchTriggers])

  useEffect(() => {
    localStorage.setItem("fixed_columns", fixedColumns);
  }, [fixedColumns])

  useEffect(() => {
    if (!showDrawer && selectedRowKey) {
      console.log("Drawer closed")
      setSelectedRowKey()
      getData()
    }
  }, [showDrawer])

  // Get data
  const getData = () => {
    if (!ongoingSearch) {
      setOngoingSearch(true)

      let filters = {}
      if (returnsStatusFilter != null) filters.returns_status = returnsStatusFilter
      if (deliveryStatusFilter != null) filters.delivery_status = deliveryStatusFilter
      if (reasonFilter != null) filters.returns_reason = reasonFilter
      if (missingFilter != null) filters.returns_missing = missingFilter
      if (invoicingFilter != null) filters.invoicing = invoicingFilter
      if (elisaFilter != null) filters.elisa_register = elisaFilter
      if (highlightFilter != null) filters.highlight = highlightFilter
      if (idFilter) filters.id = idFilter
      if (returnsItemsFilter != null) filters.returns_items = returnsItemsFilter
      if (deliveryItemsFilter != null) filters.delivery_items = deliveryItemsFilter

      getReturnsAndDeliveries(abortRef.current, dateRange[0].valueOf(), dateRange[1].endOf("day").valueOf(), searchText, filters).then(result => {
        if (result.statusCode == 200 && result.entries) {
          for (const element of result.entries) {
            // Return tags with count
            let returns_tags = {}
            element.returns_item_count = 0
            if (element.returns_items) {
              try {
                let arr = JSON.parse(element.returns_items)
                element.returns_item_count = arr.length
                arr.sort((a, b) => a.type.localeCompare(b.type))
                for (const item of arr) {
                  if (!returns_tags[item.type]) returns_tags[item.type] = 1
                  else returns_tags[item.type] += 1
                }
              }
              catch (err) {
                console.log(err)
              }
            }
            element.returns_tags = returns_tags

            // Delivery tags with count
            let delivery_tags = {}
            element.delivery_item_count = 0
            if (element.delivery_items) {
              try {
                let arr = JSON.parse(element.delivery_items)
                element.delivery_item_count = arr.length
                for (const item of arr) {
                  if (!delivery_tags[item.type]) delivery_tags[item.type] = 1
                  else delivery_tags[item.type] += 1
                }
              }
              catch (err) {
                console.log(err)
              }
            }
            element.delivery_tags = delivery_tags

            // Returns reason
            element.returns_reason_text = props.t(getReturnsReasonText(element.returns_reason))
          }

          setData(result.entries)
        }
        else {
          showToast(props.t("Error"), "error", 3000, "toast-bottom-right")
        }
      }).catch(err => {
        console.log(err)
      }).finally(() => {
        setOngoingSearch(false)
      })
    }
  }

  // Add new table row
  const addNewRow = () => {
    if (!ongoingAction) {
      setOngoingAction(true)
      addReturnsEntry(abortRef.current).then(result => {
        if (result.statusCode == 200) {
          showToast(props.t("Added"), "success", 3000, "toast-bottom-right")
          getData()
        }
        else throw new Error("Unexpected result")
      }).catch(err => {
        console.log(err)
        showToast(props.t("Error"), "error", 3000, "toast-bottom-right")
      }).finally(() => {
        setOngoingAction(false)
      })
    }
  }

  // Render item badge with count
  const getCategoryBadges = (id, count) => {
    switch (id) {
      case "home_set":
        return <span className="badge badge-soft-tertiary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("At home set")}</span>
      case "home_gw":
        return <span className="badge badge-soft-tertiary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Home gateway")}</span>
      case "home_node":
        return <span className="badge badge-soft-tertiary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Home sensor")}</span>
      case "home_accessory":
        return <span className="badge badge-soft-tertiary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Home accessory")}</span>
      case "video_device":
        return <span className="badge badge-soft-primary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Video device")}</span>
      case "video_accessory":
        return <span className="badge badge-soft-primary font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Video accessory")}</span>
      case "smartlock_device":
        return <span className="badge badge-soft-pink font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Smartlock")}</span>
      case "smartlock_accessory":
        return <span className="badge badge-soft-pink font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Smartlock accessory")}</span>
      case "tracker_device":
        return <span className="badge badge-soft-success font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Tracker")}</span>
      case "tracker_accessory":
        return <span className="badge badge-soft-success font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Tracker accessory")}</span>
      case "wristwatch_device":
        return <span className="badge badge-soft-success font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Safety wristwatch")}</span>
      case "wristwatch_accessory":
        return <span className="badge badge-soft-success font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Safety wristwatch accessory")}</span>
      case "carephone_main":
        return <span className="badge badge-soft-indigo font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Carephone")}</span>
      case "carephone_accessory":
        return <span className="badge badge-soft-indigo font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Carephone accessory")}</span>
      case "other":
        return <span className="badge badge-soft-teal font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Other")}</span>
      case "batteries":
        return <span className="badge badge-soft-teal font-size-12 py-1 px-2 me-1">{count + "x " + props.t("Batteries")}</span>
      default:
        return <></>
    }
  }

  // Table columns
  const columns = [
    {
      dataIndex: "actions",
      className: "col-sub-header-blue",
      align: "center",
      render: editFormatter,
      fixed: "left",
      width: "60px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "id",
      title: props.t("ID"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["id"], b["id"], sortOrder)
      },
      render: (cell, row) => {
        if (row.highlight == 1) {
          return (
            <Tooltip title={row.highlight_reason} trigger={["hover", "focus"]}>
              <span className="badge badge-soft-danger font-size-12 py-1 px-2">{cell} <i className="mdi mdi-alert-circle" /> </span>
            </Tooltip>
          )
        }
        else if (row.highlight == 2) {
          return (
            <Tooltip title={row.highlight_reason} trigger={["hover", "focus"]}>
              <span className="badge badge-soft-tertiary font-size-12 py-1 px-2">{cell} <i className="mdi mdi-alert-circle" /> </span>
            </Tooltip>
          )
        }
        else if (row.highlight == 3) {
          return (
            <Tooltip title={row.highlight_reason} trigger={["hover", "focus"]}>
              <span className="badge badge-soft-purple font-size-12 py-1 px-2">{cell} <i className="mdi mdi-alert-circle" /> </span>
            </Tooltip>
          )
        }
        return cell
      },
      fixed: fixedColumns ? "left" : null,
      width: "50px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "informed_at",
      title: props.t("Informed at"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["informed_at"], b["informed_at"], sortOrder)
      },
      render: formatDateSimple,
      width: "100px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "source_text",
      title: props.t("Source"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "source_text", sortOrder)
      },
      render: (cell, row) => {
        switch (row.source_type) {
          //case 1: return (<a href={"https://support.suvanto.care/scp/tickets.php?id=" + cell} target="_blank" rel="noopener noreferrer">{props.t("Ticket")}</a>)
          case 1: return (<a href={"https://support.suvanto.care/scp/tickets.php?a=search&search-type=typeahead&query=" + cell + "&number=" + cell} target="_blank" rel="noopener noreferrer">{props.t("Ticket") + " #" + cell}</a>)
          case 2: return (<a href={"/sales/orders?id=" + cell} target="_blank" rel="noopener noreferrer">{props.t("OrderAlt") + " " + cell}</a>)
          default: return (
            <span
              className="d-block"
              style={{ textOverflow: "ellipsis", maxWidth: "120px", overflow: "hidden", whiteSpace: "nowrap" }}>
              {cell}
            </span>
          )
        }
      },
      fixed: fixedColumns ? "left" : null,
      width: "140px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "organization",
      title: props.t("Organization"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "organization", sortOrder)
      },
      fixed: fixedColumns ? "left" : null,
      width: "160px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "customer",
      title: props.t("Customer"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "customer", sortOrder)
      },
      fixed: fixedColumns ? "left" : null,
      width: "160px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "contacts",
      title: props.t("Contact"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "contacts", sortOrder)
      },
      width: "160px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "returns_items",
      title: props.t("Returning items"),
      render: (cell, row) => {
        return <>
          {Object.keys(row.returns_tags).map(item => (
            getCategoryBadges(item, row.returns_tags[item])
          ))}
        </>
      },
      width: "160px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "returns_status",
      title: props.t("Returns"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["returns_status"], b["returns_status"], sortOrder)
      },
      render: (cell, row) => {
        switch (cell) {
          case 1: return <span style={{ minWidth: "80px" }} className="badge badge-soft-danger font-size-12 py-1 px-2">{props.t("Returning")}</span>
          case 2: return <span style={{ minWidth: "80px" }} className="badge badge-soft-success font-size-12 py-1 px-2">{props.t("Handled")}</span>
          default: return <span style={{ minWidth: "80px" }} className="badge badge-soft-gray font-size-12 py-1 px-2">{props.t("No returns")}</span>
        }
      },
      width: "120px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "delivery_status",
      title: props.t("Delivery"),
      align: "center",
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["delivery_status"], b["delivery_status"], sortOrder)
      },
      render: (cell, row) => {
        switch (cell) {
          case 1: return <span style={{ minWidth: "80px" }} className="badge badge-soft-tertiary font-size-12 py-1 px-2">{props.t("Waiting")}</span>
          case 2: return <span style={{ minWidth: "80px" }} className="badge badge-soft-success font-size-12 py-1 px-2">{props.t("Sent")}</span>
          default: return <span style={{ minWidth: "80px" }} className="badge badge-soft-gray font-size-12 py-1 px-2">{props.t("No delivery")}</span>
        }
      },
      fixed: fixedColumns ? "left" : null,
      width: "110px",
      ellipsis: true,
    },
    {
      className: "col-sub-header-blue",
      dataIndex: "additional_info",
      title: props.t("Additional info"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "additional_info", sortOrder)
      },
      render: (cell) => {
        return (
          <span
            className="d-block"
            style={{ textOverflow: "ellipsis", maxWidth: "240px", overflow: "hidden", whiteSpace: "nowrap" }}>
            {cell}
          </span>
        )
      },
      width: "200px",
      ellipsis: true,
    },
    {
      title: props.t("Returns").toUpperCase(),
      className: "col-header-orange",
      children: [
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_reason_text",
          title: props.t("Reason for return"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "returns_reason_text", sortOrder)
          },
          width: "200px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_additional",
          title: props.t("Additional info"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "returns_additional", sortOrder)
          },
          render: (cell) => {
            return (
              <span
                className="d-block"
                style={{ textOverflow: "ellipsis", maxWidth: "240px", overflow: "hidden", whiteSpace: "nowrap" }}>
                {cell}
              </span>
            )
          },
          width: "200px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_tracking_code",
          title: props.t("Tracking code"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "returns_tracking_code", sortOrder)
          },
          render: (cell) => { return <a href={getTrackingLink(cell)} target="_blank" rel="noopener noreferrer">{cell}</a> },
          width: "160px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_handled_at",
          title: props.t("Handled at"),
          sorter: (a, b, sortOrder) => {
            return dateSorter(a["returns_handled_at"], b["returns_handled_at"], sortOrder)
          },
          render: formatDateSimple,
          width: "120px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_handled_by",
          title: props.t("Handler"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "returns_handled_by", sortOrder)
          },
          width: "140px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-orange",
          dataIndex: "returns_missing",
          title: props.t("Missing items"),
          align: "center",
          render: cell => {
            return cell ? <i className="mdi mdi-check-bold text-primary" /> : <></>
          },
          sorter: (a, b, sortOrder) => {
            return booleanSorter(a, b, "returns_missing", sortOrder)
          },
          width: "160px",
          ellipsis: true,
        },
      ]
    },
    {
      title: props.t("Invoicing").toUpperCase(),
      className: "col-header-pink",
      children: [
        {
          className: "col-sub-header-pink",
          dataIndex: "invoicing",
          title: props.t("Invoicing"),
          align: "center",
          render: cell => {
            switch (cell) {
              case 1: return <span>{props.t("No")}</span>
              case 4: return <span style={{ minWidth: "80px" }} className="badge badge-soft-primary font-size-12 py-1 px-2">{props.t("Requires investigation")}</span>
              case 2: return <span style={{ minWidth: "80px" }} className="badge badge-soft-danger font-size-12 py-1 px-2">{props.t("Yes")}</span>
              case 3: return <span style={{ minWidth: "80px" }} className="badge badge-soft-success font-size-12 py-1 px-2">{props.t("Invoiced")}</span>
              default: return <></>
            }
          },
          sorter: (a, b, sortOrder) => {
            return numberSorter(a["invoicing"], b["invoicing"], sortOrder)
          },
          width: "110px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-pink",
          dataIndex: "invoicing_content",
          title: props.t("Invoiced items"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "invoicing_content", sortOrder)
          },
          width: "160px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-pink",
          dataIndex: "invoicing_ends_at",
          title: props.t("Invoicing ends at"),
          sorter: (a, b, sortOrder) => {
            return dateSorter(a["invoicing_ends_at"], b["invoicing_ends_at"], sortOrder)
          },
          render: formatDateSimple,
          width: "150px",
          ellipsis: true,
        },
      ]
    },
    {
      title: props.t("Deliveries").toUpperCase(),
      className: "col-header-green",
      children: [
        {
          className: "col-sub-header-green",
          dataIndex: "delivery_handled_by",
          title: props.t("Handler"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "delivery_handled_by", sortOrder)
          },
          width: "140px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-green",
          dataIndex: "delivery_items",
          title: props.t("Outgoing items"),
          render: (cell, row) => {
            return <>
              {Object.keys(row.delivery_tags).map(item => (
                getCategoryBadges(item, row.delivery_tags[item])
              ))}
            </>
          },
          width: "160px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-green",
          dataIndex: "delivery_sent_at",
          title: props.t("Sent"),
          sorter: (a, b, sortOrder) => {
            return dateSorter(a["delivery_sent_at"], b["delivery_sent_at"], sortOrder)
          },
          render: formatDateSimple,
          width: "120px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-green",
          dataIndex: "delivery_tracking_code",
          title: props.t("Tracking code"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "delivery_tracking_code", sortOrder)
          },
          render: (cell) => { return <a href={getTrackingLink(cell)} target="_blank" rel="noopener noreferrer">{cell}</a> },
          width: "160px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-green",
          dataIndex: "elisa_register",
          title: "Elisa " + props.t("Device register").toLowerCase(),
          sorter: (a, b, sortOrder) => {
            return numberSorter(a["elisa_register"], b["elisa_register"], sortOrder)
          },
          render: (cell, row) => {
            switch (cell) {
              case 1: return <span>{props.t("No need to notify")}</span>
              case 2: return <span style={{ minWidth: "80px" }} className="badge badge-soft-danger font-size-12 py-1 px-2">{props.t("Requires notification")}</span>
              case 3: return <span style={{ minWidth: "80px" }} className="badge badge-soft-success font-size-12 py-1 px-2">{props.t("Notified")}</span>
              default: return <></>
            }
          },
          width: "150px",
          ellipsis: true,
        },
        {
          className: "col-sub-header-green",
          dataIndex: "delivery_additional",
          title: props.t("Additional info"),
          sorter: (a, b, sortOrder) => {
            return stringSorter(a, b, "delivery_additional", sortOrder)
          },
          render: (cell) => {
            return (
              <span
                className="d-block"
                style={{ textOverflow: "ellipsis", maxWidth: "240px", overflow: "hidden", whiteSpace: "nowrap" }}>
                {cell}
              </span>
            )
          },
          width: "200px",
          ellipsis: true,
        },
      ]
    },
    /*{
      className: "col-sub-header-blue",
      dataIndex: "updated_at",
      title: props.t("Updated"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["updated_at"], b["updated_at"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },*/
    AntdTable.SELECTION_COLUMN,
  ]

  function editFormatter(cell, row) {
    return (
      <div className="d-inline-flex">
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            setSelectedRowKey(row.id)
            setShowDrawer(true)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </div>
    )
  }

  const onDateSelection = value => {
    setDateRange(value)
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [props.t("ID"), props.t("Informed at"), /*props.t("Source"),*/ props.t("Organization"),
      props.t("Customer"), props.t("Contacts"), props.t("Additional info"), props.t("Returns status"), props.t("Returning items"), props.t("Reason for return"), (props.t("Returns") + " " + props.t("Tracking code")),
      props.t("Handled at"), props.t("Missing items"), (props.t("Returns") + " " + props.t("Additional info")), props.t("Invoicing"), props.t("Invoiced items"),
      props.t("Invoicing ends at"), props.t("Delivery status"), props.t("Outgoing items"), (props.t("Delivery") + " " + props.t("Sent")), (props.t("Delivery") + " " + props.t("Tracking code")), (props.t("Delivery") + " " + props.t("Additional info"))
      ],
    ]

    const exportData = data.map(elt => [
      elt.id,
      formatDateSimple(elt.informed_at),
      //elt.source_text,
      elt.organization,
      elt.customer,
      elt.contacts,
      elt.additional_info,
      props.t(getReturnsStatusText(elt.returns_status)),
      prettifyItems(elt.returns_items),
      props.t(getReturnsReasonText(elt.returns_reason)),
      elt.returns_tracking_code,
      formatDateSimple(elt.returns_handled_at),
      elt.returns_missing ? props.t("Yes") : props.t("No"),
      elt.returns_additional,
      props.t(getInvoicingText(elt.invoicing)),
      elt.invoicing_content,
      formatDateSimple(elt.invoicing_ends_at),
      props.t(getDeliveryStatusText(elt.delivery_status)),
      prettifyItems(elt.delivery_items),
      formatDateSimple(elt.delivery_sent_at),
      elt.delivery_tracking_code,
      elt.delivery_additional,
    ])

    downloadExcel(title, heading, exportData)
    showToast(props.t("Excel file exported succesfully!"), "success")
  }

  // Remove some elements from items array for export
  const prettifyItems = (input) => {
    let result = input
    try {
      let temp = JSON.parse(result)
      for (const element of temp) {
        if (element.id) delete element.id
        if (element.device_id) delete element.device_id
      }
      result = JSON.stringify(temp)
    }
    catch (e) {
      console.log(e)
    }
    return result
  }

  // Table selection changed
  const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={props.t("Returns and deliveries")} />
          <Card>
            <CardBody>
              <Row>
                <Col>
                  {data != null && (
                    <React.Fragment>
                      <Row>
                        <Col className="mb-3" style={{ maxWidth: "320px" }}>
                          <Search
                            placeholder={props.t("Search")}
                            disabled={ongoingSearch}
                            allowClear
                            value={searchText}
                            onChange={v => {
                              setSearchText(v.target.value)
                            }}
                            onSearch={() => {
                              setSearchTriggers(prevValue => prevValue + 1)
                            }}
                            style={{ minWidth: "240px" }}
                          />
                        </Col>
                        <Col style={{ maxWidth: "300px" }}>
                          <div className="mb-3" style={{ minWidth: "250px" }}>
                            <CustomRangePicker
                              value={dateRange}
                              disabled={ongoingSearch}
                              rangeChanged={onDateSelection}
                              showTime={false}
                              format={"DD.MM.YYYY"}
                              allowClear={false}
                            />
                          </div>
                        </Col>
                        <Col className="col-auto mb-3">
                          <Button
                            color="primary"
                            className="compact-button"
                            id="filter-button"
                            outline
                            onClick={() => {
                              filtersOpen.length > 0
                                ? setFiltersOpen("")
                                : setFiltersOpen("1")
                            }}
                          >
                            <UncontrolledTooltip
                              placement="bottom"
                              target="filter-button"
                            >
                              {props.t("Filters")}
                            </UncontrolledTooltip>
                            {filtersOpen ? (
                              <i className="mdi mdi-filter-minus"></i>
                            ) : (
                              <i className="mdi mdi-filter-menu"></i>
                            )}
                          </Button>
                          <Button
                            className="compact-button ms-3"
                            color="primary"
                            disabled={ongoingSearch}
                            onClick={() => {
                              getData()
                            }}
                          >
                            <i className="mdi mdi-database-search me-2" />
                            {props.t("Search")}
                          </Button>
                        </Col>
                        <Col>
                          <FormGroup className="d-flex" style={{ minHeight: "32px", alignItems: "center" }}>
                            <Switch
                              id="fixedColumns"
                              name="fixedColumns"
                              checked={fixedColumns}
                              onChange={v => {
                                setFixedColumns(v)
                              }}
                              style={{}}
                              checkedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="mdi mdi-magnet-on font-size-16"
                                  />
                                </div>
                              }
                              unCheckedChildren={
                                <div
                                  className="d-flex"
                                  style={{ alignItems: "center" }}
                                >
                                  <i
                                    className="mdi mdi-magnet font-size-16"
                                  />
                                </div>
                              }
                            />
                          </FormGroup>
                        </Col>
                        <Col className="col-auto">
                          <Popconfirm
                            title={props.t("Confirm action")}
                            open={addConfirm}
                            okType="success"
                            okText={props.t("Add")}
                            cancelText={props.t("Cancel")}
                            onConfirm={() => {
                              addNewRow()
                              setAddConfirm(false)
                            }}
                            onCancel={() => {
                              setAddConfirm(false)
                            }}
                          >
                            <Button
                              className="compact-button"
                              color="success"
                              disabled={ongoingAction}
                              onClick={() => {
                                setAddConfirm(true)
                              }}
                            >
                              <i className="mdi mdi-plus me-2" />
                              {props.t("Add new")}
                            </Button>
                          </Popconfirm>
                          <Button
                            className="compact-button ms-3"
                            color="primary"
                            outline
                            disabled={ongoingSearch || data?.length == 0}
                            onClick={() => {
                              generateExcel()
                            }}
                          >
                            <i className="mdi mdi-download me-2" />
                            {props.t("Download") + " .xlsx"}
                          </Button>
                        </Col>
                      </Row>

                      <Accordion
                        flush
                        open={filtersOpen}
                        toggle={id => {
                          id === filtersOpen
                            ? setFiltersOpen("")
                            : setFiltersOpen(id)
                        }}
                      >
                        <AccordionItem>
                          <AccordionBody
                            accordionId="1"
                            className="mb-3"
                            style={{
                              border: "1px solid",
                              borderRadius: "4px",
                            }}
                          >
                            <Row className="mb-3">
                              <Col className="col-auto">
                                <span
                                  className="font-size-16 text-primary"
                                  style={{ fontWeight: "500" }}
                                >
                                  <i className="mdi mdi-filter me-1"></i>
                                  {props.t("Filters")}
                                </span>
                              </Col>
                              <Col>
                                <Button
                                  color="danger"
                                  outline
                                  size="sm"
                                  onClick={() => {
                                    setReturnsStatusFilter()
                                    setDeliveryStatusFilter()
                                    setReasonFilter([])
                                    setMissingFilter()
                                    setInvoicingFilter()
                                    setElisaFilter()
                                    setHighlightFilter()
                                    setIdFilter()
                                    setReturnsItemsFilter([])
                                    setDeliveryItemsFilter([])
                                    setSearchTriggers(prevValue => prevValue + 1)
                                  }}
                                >
                                  <i className="mdi mdi-close me-1"></i>
                                  {props.t("Clear")}
                                </Button>
                              </Col>
                            </Row>
                            <Row xs={1} sm={2} md={3} xl={4}>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Returns status")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={returnsStatusFilter}
                                  onChange={(v) => { setReturnsStatusFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("No returns") },
                                    { value: 1, label: props.t("Returning") },
                                    { value: 2, label: props.t("Handled") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Delivery status")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={deliveryStatusFilter}
                                  onChange={(v) => { setDeliveryStatusFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("No delivery") },
                                    { value: 1, label: props.t("Waiting") },
                                    { value: 2, label: props.t("Sent") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  mode="multiple"
                                  placeholder={props.t("Returning items")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={returnsItemsFilter}
                                  onChange={(v) => { setReturnsItemsFilter(v) }}
                                  options={itemOptions}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  mode="multiple"
                                  placeholder={props.t("Outgoing items")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={deliveryItemsFilter}
                                  onChange={(v) => { setDeliveryItemsFilter(v) }}
                                  options={itemOptions}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  mode="multiple"
                                  placeholder={props.t("Reason for return")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={reasonFilter}
                                  onChange={(v) => { setReasonFilter(v) }}
                                  options={reasonOptions}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Missing items")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={missingFilter}
                                  onChange={(v) => { setMissingFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("No") },
                                    { value: 1, label: props.t("Yes") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Invoicing")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={invoicingFilter}
                                  onChange={(v) => { setInvoicingFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("Undefined") },
                                    { value: 1, label: props.t("No") },
                                    { value: 2, label: props.t("Yes") },
                                    { value: 4, label: props.t("Requires investigation") },
                                    { value: 3, label: props.t("Invoiced") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  placeholder={"Elisa " + props.t("Device register").toLowerCase()}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={elisaFilter}
                                  onChange={(v) => { setElisaFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("Undefined") },
                                    { value: 1, label: props.t("No need to notify") },
                                    { value: 2, label: props.t("Requires notification") },
                                    { value: 3, label: props.t("Notified") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <Select
                                  placeholder={props.t("Highlighted row")}
                                  allowClear
                                  style={{ width: "100%" }}
                                  value={highlightFilter}
                                  onChange={(v) => { setHighlightFilter(v) }}
                                  options={[
                                    { value: 0, label: props.t("No") },
                                    { value: 1, label: props.t("Yes") },
                                  ]}
                                />
                              </Col>
                              <Col className="mb-3">
                                <AntdInput
                                  allowClear
                                  placeholder={"ID (e.g. 12,15,16)"}
                                  value={idFilter}
                                  onChange={(v) => { setIdFilter(v.target.value) }}
                                />
                              </Col>
                            </Row>
                          </AccordionBody>
                        </AccordionItem>
                      </Accordion>

                      <Row className="mt-1">
                        <Col xl="12">
                          <AntdTable
                            className="narrow-header"
                            bordered
                            size="small"
                            rowKey={"id"}
                            columns={columns}
                            dataSource={data}
                            rowSelection={{
                              type: "radio",
                              fixed: false,
                              selectedRowKeys: selectedRowKeys,
                              onChange: onSelectChange,
                              columnWidth: 0,
                              renderCell: () => "",
                            }}
                            onRow={(record, rowIndex) => {
                              return {
                                onClick: event => {
                                  if (
                                    selectedRowKeys.includes(record.id)
                                  ) {
                                    setSelectedRowKeys([])
                                  } else {
                                    setSelectedRowKeys([record.id])
                                  }
                                },
                              }
                            }}
                            pagination={{
                              showTotal: (total) => `${total} ${props.t("found results")}`,
                              showSizeChanger: true,
                              defaultPageSize: 100,
                              pageSizeOptions: defaultPageSizeOptions,
                            }}
                            loading={ongoingSearch}
                            tableLayout="auto"
                            scroll={{
                              x: "max-content",
                              y: tableDefaultHeight,
                            }}
                          />
                        </Col>
                      </Row>
                    </React.Fragment>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
      <ReturnsForm id={selectedRowKey} showDrawer={showDrawer} setShowDrawer={setShowDrawer} />
    </>
  )
}

export const getReturnsReasonText = (id) => {
  switch (id) {
    case 1: return "Service ends"
    case 2: return "Faulty device"
    case 3: return "Exchange"
    case 4: return "Operator change"
    case 5: return "Broken device"
    case 6: return "Lost"
    case 7: return "Swollen battery"
    case 8: return "Unwarranted return"
    case 9: return "Other"
    default: return ""
  }
}

export const getInvoicingText = (id) => {
  switch (id) {
    case 1: return "No"
    case 2: return "Yes"
    case 3: return "Invoiced"
    case 4: return "Requires investigation"
    default: return ""
  }
}

export const getReturnsStatusText = (id) => {
  switch (id) {
    case 1: return "Returning"
    case 2: return "Handled"
    default: return "No returns"
  }
}

export const getDeliveryStatusText = (id) => {
  switch (id) {
    case 1: return "Waiting"
    case 2: return "Sent"
    default: return "No delivery"
  }
}

ReturnsManagement.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(ReturnsManagement)
